import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

export type RestaurantLogoSize = 'small' | 'medium' | 'large';

// Box + initial text size per named size. Kept in one map so every render site
// stays visually in sync (same idea as SupplierLogo's DIMENSIONS_BY_SIZE).
const SIZE_CLASSES: Record<RestaurantLogoSize, { box: string; text: string }> = {
  small: { box: 'h-8 w-8', text: 'text-xs' },
  medium: { box: 'h-11 w-11', text: 'text-sm' },
  large: { box: 'h-14 w-14', text: 'text-lg' },
};

export interface RestaurantLogoProps {
  /** Full public URL, or null when the restaurant has no logo. */
  logoUrl: string | null | undefined;
  /** Restaurant name — alt text and the source of the fallback initial. */
  name: string;
  size?: RestaurantLogoSize;
  className?: string;
}

/**
 * Single render point for restaurant logos. Renders the uploaded image
 * `object-contain` inside a neutral tile (user-uploaded logos can't use the
 * generated supplier dark-mode treatments, so the tile stays light in both
 * themes — cf. getSupplierLogoTileWrapperClasses in helperService.ts).
 *
 * No logo, or a URL that fails to load, falls back to the NavBar-style initial
 * circle — never an empty box.
 */
export const RestaurantLogo: React.FC<RestaurantLogoProps> = ({
  logoUrl,
  name,
  size = 'medium',
  className,
}) => {
  const [failed, setFailed] = useState(false);
  const { box, text } = SIZE_CLASSES[size];

  // A replaced logo gets a new uuid URL — clear a previous failure so the new
  // image gets a chance to render.
  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);

  if (!logoUrl || failed) {
    const initial = name.trim().charAt(0) || '?';
    return (
      <div
        title={name}
        aria-label={name}
        className={cn(
          box,
          text,
          'rounded-full bg-gray-400 dark:bg-gray-500 flex items-center justify-center text-white dark:text-white uppercase flex-shrink-0',
          className
        )}
      >
        {initial}
      </div>
    );
  }

  return (
    <div
      title={name}
      className={cn(
        box,
        // Light tile in both themes on purpose: white-background logos would
        // otherwise glare or disappear on the dark canvas.
        'rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 bg-zinc-100 dark:bg-zinc-100 border border-zinc-200 dark:border-white/10',
        className
      )}
    >
      <img
        src={logoUrl}
        alt={name}
        className="w-full h-full object-contain"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

export default RestaurantLogo;
